type GuideBuyButtonMount = {
  button: HTMLButtonElement;
  label: HTMLElement | null;
  variantId: string;
  originalLabel: string;
  lockedMinWidth: string | null;
};

type ShopifyCartCreateResponse = {
  data?: {
    cartCreate?: {
      cart?: {
        checkoutUrl?: string;
      };
      userErrors?: Array<{
        field?: string[];
        message?: string;
      }>;
    };
  };
};

const SHOPIFY_STOREFRONT_API_URL =
  'https://late-round-fantasy-football.myshopify.com/api/2024-01/graphql.json';
const SHOPIFY_STOREFRONT_ACCESS_TOKEN = 'e8b7de1d0873ecca9f08e97687732347';

export default class GuideBuyButton {
  private mounts: GuideBuyButtonMount[];
  private checkoutUrls = new Map<string, string>();
  private checkoutUrlPromises = new Map<string, Promise<string>>();

  constructor() {
    this.mounts = this.getMounts();

    if (!this.mounts.length) {
      return;
    }

    this.init();
  }

  private getMounts(): GuideBuyButtonMount[] {
    return Array.from(
      document.querySelectorAll<HTMLButtonElement>('[data-guide-buy-button]'),
    )
      .map((button) => {
        const variantId = button.dataset.shopifyVariantId?.trim() ?? '';

        if (!variantId) {
          return null;
        }

        const label = button.querySelector<HTMLElement>('span');
        const originalLabel = label?.textContent?.trim() ?? button.textContent?.trim() ?? '';

        return {
          button,
          label,
          variantId,
          originalLabel,
          lockedMinWidth: null,
        };
      })
      .filter((mount): mount is GuideBuyButtonMount => mount !== null);
  }

  private init() {
    const variantIds = new Set(this.mounts.map((mount) => mount.variantId));

    variantIds.forEach((variantId) => {
      const variantMounts = this.mounts.filter((mount) => mount.variantId === variantId);

      variantMounts.forEach((mount) => {
        this.setLoadingState(mount, true);
      });

      this.getCheckoutUrlPromise(variantId)
        .then((checkoutUrl) => {
          this.checkoutUrls.set(variantId, checkoutUrl);

          variantMounts.forEach((mount) => {
            this.setLoadingState(mount, false);
          });
        })
        .catch((error) => {
          console.error('Guide checkout preload failed.', error);

          variantMounts.forEach((mount) => {
            this.setLoadingState(mount, false);
          });
        });
    });

    this.mounts.forEach((mount) => {
      mount.button.addEventListener('click', () => {
        this.handleClick(mount);
      });
    });
  }

  private async handleClick(mount: GuideBuyButtonMount) {
    if (mount.button.disabled) {
      return;
    }

    const preloadedCheckoutUrl = this.checkoutUrls.get(mount.variantId);

    if (preloadedCheckoutUrl) {
      window.open(preloadedCheckoutUrl, '_blank', 'noopener');
      return;
    }

    this.setLoadingState(mount, true);

    try {
      const checkoutUrl = await this.getCheckoutUrlPromise(mount.variantId);

      if (!checkoutUrl) {
        throw new Error('Shopify did not return a checkout URL.');
      }

      this.checkoutUrls.set(mount.variantId, checkoutUrl);
      window.open(checkoutUrl, '_blank', 'noopener');
    } catch (error) {
      console.error('Guide checkout failed.', error);
      this.setLoadingState(mount, false);
      window.alert('Unable to start checkout right now. Please try again.');
    }
  }

  private setLoadingState(mount: GuideBuyButtonMount, isLoading: boolean) {
    mount.button.disabled = isLoading;
    mount.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    if (isLoading) {
      if (mount.lockedMinWidth === null) {
        mount.lockedMinWidth = mount.button.style.minWidth;
      }

      mount.button.style.minWidth = `${mount.button.offsetWidth}px`;
    } else if (mount.lockedMinWidth !== null) {
      if (mount.lockedMinWidth === '') {
        mount.button.style.removeProperty('min-width');
      } else {
        mount.button.style.minWidth = mount.lockedMinWidth;
      }
      mount.lockedMinWidth = null;
    }

    if (mount.label) {
      mount.label.textContent = isLoading ? 'Loading...' : mount.originalLabel;
    }
  }

  private getCheckoutUrlPromise(variantId: string): Promise<string> {
    const existingPromise = this.checkoutUrlPromises.get(variantId);

    if (existingPromise) {
      return existingPromise;
    }

    const checkoutUrlPromise = this.createCartCheckoutUrl(variantId).catch((error) => {
      this.checkoutUrlPromises.delete(variantId);
      throw error;
    });

    this.checkoutUrlPromises.set(variantId, checkoutUrlPromise);

    return checkoutUrlPromise;
  }

  private async createCartCheckoutUrl(variantId: string): Promise<string> {
    const merchandiseId = `gid://shopify/ProductVariant/${variantId}`;
    const response = await fetch(SHOPIFY_STOREFRONT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({
        query: `
          mutation GuideCartCreate($input: CartInput!) {
            cartCreate(input: $input) {
              cart {
                checkoutUrl
              }
              userErrors {
                field
                message
              }
            }
          }
        `,
        variables: {
          input: {
            lines: [
              {
                quantity: 1,
                merchandiseId,
              },
            ],
          },
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Shopify request failed with status ${response.status}.`);
    }

    const payload = (await response.json()) as ShopifyCartCreateResponse;
    const userErrors = payload.data?.cartCreate?.userErrors ?? [];

    if (userErrors.length) {
      throw new Error(userErrors.map((error) => error.message).filter(Boolean).join(' '));
    }

    return payload.data?.cartCreate?.cart?.checkoutUrl ?? '';
  }
}
